import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Health metric types
export type HealthMetricType = 'sleep' | 'hrv' | 'heart_rate' | 'readiness' | 'activity';

// Sleep trend data point
export interface SleepTrendPoint {
  date: string;
  duration: number;
  quality: 'poor' | 'fair' | 'good' | 'excellent' | null;
  deep_sleep: number | null;
  rem_sleep: number | null;
  light_sleep: number | null;
}

// HRV trend data point
export interface HrvTrendPoint {
  date: string;
  value: number;
  rolling_avg: number | null;
}

// Heart rate trend data point
export interface HeartRateTrendPoint {
  date: string;
  resting: number;
  average: number | null;
  max: number | null;
  min: number | null;
}

// Readiness trend data point
export interface ReadinessTrendPoint {
  date: string;
  score: number;
  sleep_score: number | null;
  recovery_score: number | null;
  activity_score: number | null;
  hrv_score: number | null;
  recommendation: string | null;
}

// Activity trend data point
export interface ActivityTrendPoint {
  date: string;
  steps: number;
  distance_km: number | null;
  active_calories: number | null;
  workout_minutes: number | null;
}

// Union type for all trend points
export type HealthTrendPoint =
  | SleepTrendPoint
  | HrvTrendPoint
  | HeartRateTrendPoint
  | ReadinessTrendPoint
  | ActivityTrendPoint;

interface UseHealthTrendsOptions {
  days?: number;
}

export function useHealthTrends<T extends HealthTrendPoint>(
  clientId: string | undefined,
  metric: HealthMetricType,
  options: UseHealthTrendsOptions = {}
) {
  const { days = 30 } = options;
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTrends = useCallback(async () => {
    if (!clientId) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call the SQL function to get health trends
      const { data: result, error: queryError } = await supabase
        .rpc('get_health_trends', {
          p_client_id: clientId,
          p_metric: metric,
          p_days: days,
        });

      if (queryError) throw queryError;

      setData((result || []) as T[]);
    } catch (err) {
      console.error('Error loading health trends:', err);
      setError('Failed to load health trends');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, metric, days]);

  useEffect(() => {
    loadTrends();
  }, [loadTrends]);

  return { data, loading, error, reload: loadTrends };
}

// Specialized hooks with proper typing
export function useSleepTrends(clientId: string | undefined, days = 30) {
  return useHealthTrends<SleepTrendPoint>(clientId, 'sleep', { days });
}

export function useHrvTrends(clientId: string | undefined, days = 30) {
  return useHealthTrends<HrvTrendPoint>(clientId, 'hrv', { days });
}

export function useHeartRateTrends(clientId: string | undefined, days = 30) {
  return useHealthTrends<HeartRateTrendPoint>(clientId, 'heart_rate', { days });
}

export function useReadinessTrends(clientId: string | undefined, days = 30) {
  return useHealthTrends<ReadinessTrendPoint>(clientId, 'readiness', { days });
}

export function useActivityTrends(clientId: string | undefined, days = 30) {
  return useHealthTrends<ActivityTrendPoint>(clientId, 'activity', { days });
}
